import classNames from 'classnames';
import i18n from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// import 'react-chat-widget/lib/styles.css';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { fetchToken, fetchUser } from './apis/auth.js';
import { closeTicket, establishWebSocket } from './apis/webSocket.js';
// import './App.css';
import EndChatPrompt from './EndChatPrompt';
import Message from './Message.jsx';
import ConnectedWidget from './react-chat-widget/index.tsx';
import { deleteMessages, markAllMessagesRead2, renderCustomComponent, turnOffMsgLoader, turnOnMsgLoader } from './react-chat-widget/store/dispatcher.ts';
import SessionExtension from './SessionExtension.jsx';

//receive language from parent and translate
let setReceivedLanguage;
const receivedLanguage = new Promise((resolve) => { setReceivedLanguage = resolve });
window.addEventListener('message', event => {
  if (event.source === window.parent) {
    const { type, url, lang } = event.data;

    if (type === 'url') {
      const parsedUrl = new URL(url);
      if (parsedUrl.pathname.startsWith('/tc')) { // /en for English
        i18n.changeLanguage('zh-HK');
      } else {
        i18n.changeLanguage('en-HK');
      }
      setReceivedLanguage();
    } else if (type === 'mobile_open') {
      if (lang === 'tc') {
        i18n.changeLanguage('zh-HK');
      } else {
        i18n.changeLanguage('en-HK');
      }
      setReceivedLanguage();
    }
  }
});
setTimeout(setReceivedLanguage, 3000); // default to english after 3 seconds


function notifyParent(message) {
  console.log(`type: ${message.type}`);
  window.parent?.postMessage(message, '*');
}
notifyParent({ type: 'init' });


const PING_INTERVAL = 20 * 1000; // 20 seconds
const MAX_WS_RECONNECT_TRIES = 6;
const IDLE_DELAY = 20 * 60 * 1000; // 20 minutes
const IDLE_AUTO_CLOSE_DELAY = 2 * 60 * 1000; // 2 minutes
const SHOW_BUSY_MESSAGE_TIMEOUT = 1 * 60 * 1000; // 1 minute

const TICKET_CLOSE_CODES = [4101, 4102, 4103, 4104];

const PLACEHOLDER_DATE = new Date(new Date().getTime() + IDLE_DELAY);

function getCurrentTimestamp() {
  return new Date().toISOString().slice(0, -1); // remove trailing Z
}

function App() {
  const { t } = useTranslation();
  const socket = useRef();
  const [socketIsOpen, setSocketIsOpen] = useState(false);
  const [isClosedWidget, setIsClosedWidget] = useState(true);
  const [hasChatStarted, setHasChatStarted] = useState(false);
  const [hasChatEnded, setHasChatEnded] = useState(false);
  const [currentEndPromptType, setCurrentEndPromptType] = useState(null);
  const [message, setMessage] = useState("");
  const textarea = useRef();
  const textareaListeners = useRef([]);
  const [history, setHistory] = useState([]);
  const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);
  const currentId_ticket = useRef();
  const deviceTypeRef = useRef('Undefine');

  const idleAt = useRef(PLACEHOLDER_DATE);

  const trimmedMessage = message.trim()
  const findLast = (arr, predicate) => arr.slice().reverse().find(predicate);
  const latestMessage = findLast(history, (x) => x.role_sender === 'client')
  const latestMessageOrResponse = latestMessage ? findLast(history, (x) => x.role_sender === 'client' || x.response_to === latestMessage.id) : true
  const latestMessageHasResponse = latestMessage !== latestMessageOrResponse // also true if no latest message exists

  useEffect(() => {
    deviceTypeRef.current = navigator.userAgent;
  }, []);

  // cannot send while "do you want to end the chat" prompt is open
  // cannot send if message is blank
  // cannot send if latest message (if any) has not been responded to yet
  // if all of the above are false, then:
  // can send if socket is open
  // can send if chat hasn't started yet (new socket will be opened)
  const canSend = !currentEndPromptType
    && trimmedMessage.length > 0
    && latestMessageHasResponse
    && (socketIsOpen || !hasChatStarted);

  // helper methods
  const addHistory = useCallback((message) => {
    setHistory((prevHistory) => [...prevHistory, message])
  }, [])

  const addCustomMessage = useCallback((message) => {
    renderCustomComponent(Message, message);
  }, [])

  const sendTnc = useCallback(() => {
    const customMessageProps = {
      text: t('tnc'),
      role_sender: "response",
      timestamp: null,
    }
    //addResponseMessage(`${t('tnc')}`);
    addCustomMessage(customMessageProps);
  }, [t, addCustomMessage])

  const showConfirmEndPrompt = () => {
    setCurrentEndPromptType("confirm");
  }

  function sendKeepalive() {
    if (socket.current) {
      socket.current.send(JSON.stringify({}))
    } else {
      console.error('cannot keep alive connection since socket does not exist')
    }
  }

  // ask users whether they extend the session
  useEffect(() => {
    // button clicked callback
    function closeSelected(shouldClose) {
      // TODO error if socket is not connected??
      if (shouldClose) {
        closeTicket(currentId_ticket.current).then(result => {
          if (!result) {
            // TODO notify user
            console.error('failed to close ticket');
          }
          turnOffMsgLoader();
          clearTimeout(loadingTimeoutId);
        });
      } else {
        updateIdleAt(); // prevent race condition where end prompt shows up again
        sendKeepalive();
      }
      setCurrentEndPromptType(null);
    }

    function closeForIdle() {
      closeTicket(currentId_ticket.current, 4101).then(result => {
        if (!result) {
          // TODO notify user
          console.error('failed to close ticket');
        }
        turnOffMsgLoader();
      });
    }

    if (currentEndPromptType === "confirm") {
      renderCustomComponent(EndChatPrompt, { closeSelected }, false, "close-prompt");

      return () => {
        deleteMessages(1, "close-prompt"); // remove prompt
      }
    } else if (currentEndPromptType === "idle") {
      renderCustomComponent(SessionExtension, { closeSelected }, false, "close-prompt");
      const timeoutId = setTimeout(() => {
        closeForIdle();
      }, IDLE_AUTO_CLOSE_DELAY);

      return () => {
        deleteMessages(1, "close-prompt"); // remove prompt
        clearTimeout(timeoutId);
      }
    }
  }, [currentEndPromptType])

  // fetch user
  useEffect(() => {
    const getToken = async () => {
      try {
        let token = 'null';
        if (localStorage.getItem("jwt")) {
          token = localStorage.getItem("jwt");
          console.log("token", token);
        }
        const userState = await fetchToken(token);
        if (userState) {
          console.log("userState", userState);
          localStorage.setItem("jwt", userState.access_token);
          //Cookies.set('jwt', userState.access_token);
          getUser(userState.access_token);
        }

      } catch {
        console.error("failed to get client token")
      }
    }
    const getUser = async (accessToken) => {
      let userData;
      userData = await fetchUser(accessToken);
      if (userData) {
        currentId_ticket.current = userData.active_ticket;
        await receivedLanguage;
        sendTnc();
        if (userData.active_ticket) {
          startChat();
        }
      }
    }

    if (localStorage.getItem("jwt") !== null) {
      getUser(localStorage.getItem("jwt"));
    }
    else {
      getToken();
    }

    return () => {
      // clean up for hot reload
      deleteMessages(99999); // arbitrary large number to clear messages
      setHistory([]);
      socket.current?.close?.(4999);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateIdleAt() {
    idleAt.current.setTime(new Date().getTime() + IDLE_DELAY);
  }

  const startChat = useCallback(async (firstMessage = undefined) => {
    if (socket.current) {
      console.error("Attempted to start chat more than once");
      return;
    }
    setHasChatStarted(true);
    let reconnectTries = 0;

    const getWebSocket = () => {

      const promise = new Promise((resolve, reject) => {
        const token = localStorage.getItem("jwt");
        const webSocket = establishWebSocket(token, deviceTypeRef.current);
        socket.current = webSocket;

        let receivedPong = false;
        let pingIntervalId;
        let shouldPing = true;
        //listen new message
        webSocket.onmessage = (event) => {
          let json_received = JSON.parse(event.data);
          if (json_received.type === 'pong') {
            receivedPong = true;
            reconnectTries = 0;
            return;
          }
          setCurrentEndPromptType(null);
          updateIdleAt();
          if (json_received.role_sender === "agent") {
            turnOffMsgLoader();
            markAllMessagesRead2();
            const timeoutIds = setTimeout(() => { }, 0);
            for (let i = 0; i < timeoutIds; i++) {
              if (i != pingIntervalId) {
                clearTimeout(i);
              }
            }
            addCustomMessage(json_received)
            addHistory(json_received)
            // TODO don't add message until after received init
          } else if (json_received.role_sender === 'client') {
            currentId_ticket.current = json_received.id_ticket;
            if (json_received.text) {
              addCustomMessage(json_received)
              addHistory(json_received)
            }
          } else if (json_received.type === 'init') {
            // assume there's only one ticket
            let messages = Object.values(json_received.messages)[0];
            messages.filter(m => m.text).forEach(addCustomMessage);
            messages.filter(m => m.text).forEach(addHistory);
            // (empty messages represent keepalive requests)
          } else if (json_received.role_sender === 'system') {
            addCustomMessage(json_received)
            // webSocket.close(4000, 'ticket closed');
          }
        };

        webSocket.onopen = (event) => {
          console.log("socket opened");
          setSocketIsOpen(true)
          // NOTE: do not reset reconnect tries here; wait until receiving pong
          // (in case ping or init causes an error)
          resolve(webSocket);

          // reset history so that the init message doesn't duplicate the history after a disconnect
          deleteMessages(99999);
          setHistory([])
          sendTnc();
          // alternate sending ping and checking for pong
          pingIntervalId = setInterval(() => {
            if (shouldPing) {
              if (webSocket.readyState === webSocket.OPEN) {
                webSocket.send(JSON.stringify({ type: "ping" }));
                receivedPong = false;
                shouldPing = false;
              } else {
                webSocket.close();
              }
            } else {
              if (receivedPong) {
                shouldPing = true;
              } else {
                webSocket.close()
              }
            }
            if (new Date() > idleAt.current) {
              setCurrentEndPromptType("idle");
            }
          }, PING_INTERVAL / 2);
        }

        webSocket.onclose = (event) => {
          clearInterval(pingIntervalId);
          socket.current = undefined;
          setSocketIsOpen(false);
          setCurrentEndPromptType(null);
          const closeCodePrefix = Math.floor(event.code / 1000);
          if (TICKET_CLOSE_CODES.includes(event.code)) {
            let reason = t(`endCode${event.code}`, null)
            if (reason) {
              reason += '\n\n';
            } else {
              reason = '';
            }
            turnOffMsgLoader();
            markAllMessagesRead2();
            const timeoutIds = setTimeout(() => { }, 0);
            for (let i = 0; i < timeoutIds; i++) {
              clearTimeout(i);
            }
            setHasChatEnded(true);
            let date = new Date().toLocaleString();
            let ticketId = currentId_ticket.current;
            addCustomMessage({
              text: reason + t('endMessage', { date, ticketId }),
              role_sender: 'system',
              timestamp: getCurrentTimestamp()
            })
          }
          else if (closeCodePrefix === 1) { //accidentally closed
            turnOffMsgLoader();
            markAllMessagesRead2();
            const timeoutIds = setTimeout(() => { }, 0);
            for (let i = 0; i < timeoutIds; i++) {
              clearTimeout(i);
            }
            if (reconnectTries < MAX_WS_RECONNECT_TRIES) {
              reconnectTries++;
              setTimeout(getWebSocket, 2 ^ (reconnectTries) * 3000); // exponential backoff
            }
          }
        };

      });

      return promise;
    }


    console.log("Connecting to WebSocket server...");
    const ws = await getWebSocket();

    if (firstMessage) {
      ws.send(firstMessage);
    }
  }, [sendTnc, addCustomMessage, addHistory, t]);

  // render textarea for typing messages instead of using the library one
  useEffect(() => {
    if (isClosedWidget) {
      return;
    }
    function handleMessageOnChange() {
      // Note: this event handler does not have access to the latest state
      const target = textarea.current;
      target.style.height = '';
      target.style.height = target.scrollHeight + 'px';
      setMessage(target.value);
    }

    function maybeSendMessage(message) {
      if (!canSend) {
        console.warn('Cannot send message because websocket disconnected or ???');
        return false;
      }

      const payload = JSON.stringify({
        message_text: message,
      });
      if (socket.current) {
        socket.current.send(payload);
      } else {
        startChat(payload);
      }
      return true;
    }

    function handleSendMessage(event) {
      // prefer reading from textarea rather than state so that these handlers do not need to be
      // re-created each time the message changes
      const textareaMessage = textarea.current.value.trim();
      if ((event.key === "Enter" && !event.shiftKey) || event.type === "click") {
        const markdownInjectionPattern = /\[.*?\]\(.*?\)|\!\[.*?\]/;
        if (markdownInjectionPattern.test(textareaMessage)) {
          textarea.current.value = '';
          handleMessageOnChange();
          textarea.current.focus();
          const customMessage = {
            text: t('invalidMessage'),
            role_sender: "agent",
            timestamp: null,
          };
          addCustomMessage(customMessage);
          addHistory(customMessage);
        }
        else {
          event.preventDefault();

          const didSend = maybeSendMessage(textareaMessage)

          if (didSend) {
            // clear value in UI
            textarea.current.value = '';
            handleMessageOnChange();
            textarea.current.focus(); // re-focus textarea after clicking send button

            let randomTimeout = Math.floor(Math.random() * (5000 - 1000 + 1)) + 1000;
            const id = setTimeout(() => {
              turnOnMsgLoader();
              markAllMessagesRead2();
              setTimeout(() => {
                turnOffMsgLoader();
                const customMessage = {
                  text: t('busyMessage'),
                  role_sender: "agent",
                  timestamp: null,
                };
                addCustomMessage(customMessage);
                addHistory(customMessage);
                randomTimeout = Math.floor(Math.random() * (5000 - 1000 + 1)) + 1000;

                setTimeout(() => {
                  turnOnMsgLoader();
                  markAllMessagesRead2();
                }, randomTimeout);
              }, SHOW_BUSY_MESSAGE_TIMEOUT);
            }, randomTimeout);
            setLoadingTimeoutId(id);
          }
        }
      }
    }
    const appNode = document.getElementById("App");

    // replace default input with custom textarea
    const rcwNewMessageElement = appNode.getElementsByClassName("rcw-new-message")[0];
    let textareaElement;
    if (rcwNewMessageElement) {
      textareaElement = document.createElement("textarea");
      textarea.current = textareaElement;

      textareaElement.setAttribute("placeholder", "Type a message...");
      textareaElement.setAttribute("class", "textarea-send");
      textareaElement.setAttribute("rows", "1");
      textareaElement.addEventListener("keydown", handleSendMessage);
      textareaElement.addEventListener("input", handleMessageOnChange);
      textareaListeners.current = [["keydown", handleSendMessage], ["input", handleMessageOnChange]];

      // initialize text content
      textareaElement.value = message;

      // update height after adding to DOM (so that inherited CSS values get applied properly)
      rcwNewMessageElement.replaceWith(textareaElement);
      handleMessageOnChange();

      textareaElement.focus();
    } else {
      textareaElement = textarea.current
      textareaListeners.current.forEach(([event, listener]) => {
        textareaElement.removeEventListener(event, listener)
      })
      textareaElement.addEventListener("keydown", handleSendMessage);
      textareaElement.addEventListener("input", handleMessageOnChange);
      textareaListeners.current = [["keydown", handleSendMessage], ["input", handleMessageOnChange]];
    }

    // replace send button event handler
    const sender = appNode.getElementsByClassName("rcw-send-icon")[0];
    sender.style.transform = "scale(1.3)";
    sender.onclick = handleSendMessage;
  }, [isClosedWidget, canSend, startChat])


  const handleToggle = (isOpen) => {
    setIsClosedWidget(!isOpen);
    notifyParent({ type: isOpen ? 'show' : 'hide' });
    if (hasChatEnded) {
      // reset
      setHasChatStarted(false);
      setHasChatEnded(false);
      deleteMessages(99999);
      setHistory([])
      sendTnc();
    }
  }


  useEffect(() => {
    const appNode = document.getElementById('App');
    const sender = appNode.getElementsByClassName('rcw-send-icon');
    if (sender.length > 0) {
      sender[0].src = canSend ? './sender_typed.svg' : './sender.svg';
    }
  }, [canSend, isClosedWidget]) // isClosedWidget needed to update initial image on open

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route index element={<>
            <div id="App" className={classNames({
              "closed-chat": hasChatEnded,
              "hide-send": currentEndPromptType,
            })}>
              <ConnectedWidget
                closeChat={!isClosedWidget && hasChatStarted && !hasChatEnded ? showConfirmEndPrompt : null}
                handleToggle={handleToggle}
                title=""
                subtitle={t('customerSupport')}
              />
              {/* <button onClick={() => { toggleWidget(); }} className='toggle-widget-btn'>
                  Toggle Widget
                </button> */}
              {/* {
                  !isClosedWidget && hasChatStarted && !hasChatEnded ? (
                    <button onClick={() => { console.log("clicked"); showConfirmEndPrompt(); }} className='transparent-btn'>
                    </button>
                  )
                    :
                    (
                      <>
                      </>
                    )
                  } */}
            </div>
          </>}>

          </Route>


          {/* <Route path="/chatbot-tnc" element={<TermNCondition />}>

            </Route> */}
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
